import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bot, User, Loader2, Lightbulb, Send, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAIQuota } from "@/hooks/useAIQuota";
import { QuotaBanner } from "./QuotaBanner";
import { PaywallDialog } from "./PaywallDialog";
import { cn } from "@/lib/utils";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface TutorDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  moduleId: string;
  moduleTitle: string;
  category: string;
  contextType?: "module" | "video" | "quiz";
  contextRefId?: string;
  videoTitle?: string;
  lessonContent?: string;
}

const SUGGESTIONS = [
  "Explica esse conceito de outro jeito",
  "Me dá um exemplo prático",
  "Quais erros comuns iniciantes cometem?",
  "Cria 3 perguntas pra eu praticar",
];

const SUPABASE_URL = "https://jdwcgbwcwkrrvaqtokju.supabase.co";

export function TutorDrawer({
  isOpen,
  onClose,
  moduleId,
  moduleTitle,
  category,
  contextType = "module",
  contextRefId,
  videoTitle,
  lessonContent,
}: TutorDrawerProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const quota = useAIQuota();

  // Carrega histórico ao abrir
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      const { data } = await supabase
        .from("ai_tutor_conversations")
        .select("messages")
        .eq("user_id", user.id)
        .eq("module_id", moduleId)
        .eq("context_type", contextType)
        .eq("context_ref_id", contextRefId ?? null)
        .maybeSingle();

      if (cancelled) return;

      if (data?.messages && Array.isArray(data.messages) && (data.messages as any[]).length > 0) {
        setMessages((data.messages as any[]).map((m) => ({ role: m.role, content: m.content })));
      } else {
        setMessages([
          {
            role: "assistant",
            content: `Olá! 👋 Sou seu **Tutor IA**. Estou acompanhando a aula **"${videoTitle || moduleTitle}"**. Pode me perguntar qualquer coisa sobre o conteúdo, pedir exemplos, código ou questões pra praticar.`,
          },
        ]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, moduleId, contextType, contextRefId, moduleTitle, videoTitle]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const persistAssistant = useCallback(
    async (content: string) => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        await fetch(`${SUPABASE_URL}/functions/v1/save-tutor-message`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            moduleId,
            contextType,
            contextRefId,
            role: "assistant",
            content,
          }),
        });
      } catch (e) {
        console.error("persist assistant", e);
      }
    },
    [moduleId, contextType, contextRefId]
  );

  const sendMessage = async (text: string) => {
    if (!text.trim() || isLoading) return;
    if (quota.tutorExhausted) {
      setShowPaywall(true);
      return;
    }

    const userMsg: Message = { role: "user", content: text };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInputValue("");
    setIsLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Faça login para usar o Tutor IA");

      const response = await fetch(`${SUPABASE_URL}/functions/v1/quiz-tutor-ai`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          moduleId,
          moduleTitle,
          contextType,
          contextRefId,
          videoTitle,
          lessonContent,
          category,
          messages: updated.map((m) => ({ role: m.role, content: m.content })),
        }),
      });

      if (response.status === 429) {
        const err = await response.json().catch(() => ({}));
        if (err?.code === "QUOTA_EXCEEDED") {
          setShowPaywall(true);
          setMessages((prev) => prev.slice(0, -1));
          return;
        }
        throw new Error(err?.error || "Muitas requisições. Tente em instantes.");
      }
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.error || "Erro ao conectar com o tutor");
      }
      if (!response.body) throw new Error("Resposta vazia");

      quota.refresh();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = "";
      let buffer = "";

      setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

      const flush = (line: string) => {
        if (line.endsWith("\r")) line = line.slice(0, -1);
        if (line.startsWith(":") || line.trim() === "") return;
        if (!line.startsWith("data: ")) return;
        const jsonStr = line.slice(6).trim();
        if (jsonStr === "[DONE]") return;
        try {
          const parsed = JSON.parse(jsonStr);
          const delta = parsed.choices?.[0]?.delta?.content;
          if (delta) {
            assistantContent += delta;
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: "assistant", content: assistantContent };
              return next;
            });
          }
        } catch {
          /* parser parcial */
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl: number;
        while ((nl = buffer.indexOf("\n")) !== -1) {
          flush(buffer.slice(0, nl));
          buffer = buffer.slice(nl + 1);
        }
      }
      if (buffer.trim()) buffer.split("\n").forEach(flush);

      if (assistantContent) {
        await persistAssistant(assistantContent);
      }
    } catch (e) {
      console.error("Tutor error:", e);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: e instanceof Error ? e.message : "Erro inesperado." },
      ]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(o) => !o && onClose()}>
        <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col p-0 gap-0">
          <SheetHeader className="p-4 border-b bg-gradient-to-r from-primary/10 to-primary/5">
            <SheetTitle className="flex items-center gap-2">
              <div className="p-2 rounded-full bg-primary/20">
                <Bot className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-base block">Tutor IA</span>
                <p className="text-xs text-muted-foreground font-normal truncate">
                  {videoTitle || moduleTitle}
                </p>
              </div>
              <QuotaBanner
                used={quota.tutor_used}
                limit={quota.tutor_limit}
                remaining={quota.tutor_remaining}
                isUnlimited={quota.isUnlimited}
                variant="compact"
              />
            </SheetTitle>
          </SheetHeader>

          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={cn("flex gap-3", m.role === "user" && "flex-row-reverse")}>
                  <div
                    className={cn(
                      "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
                      m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"
                    )}
                  >
                    {m.role === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                  </div>
                  <div
                    className={cn(
                      "px-4 py-3 rounded-2xl max-w-[85%] text-sm",
                      m.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted prose prose-sm dark:prose-invert max-w-none prose-p:my-2 prose-pre:my-2 prose-code:text-xs prose-headings:my-2"
                    )}
                  >
                    {m.role === "assistant" ? (
                      m.content ? (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                      ) : (
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" /> Pensando...
                        </span>
                      )
                    ) : (
                      <span className="whitespace-pre-wrap">{m.content}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          {quota.tutorExhausted && (
            <div className="px-4 pb-2">
              <QuotaBanner
                used={quota.tutor_used}
                limit={quota.tutor_limit}
                remaining={quota.tutor_remaining}
                isUnlimited={quota.isUnlimited}
                onUpgrade={() => setShowPaywall(true)}
                variant="full"
              />
            </div>
          )}

          {messages.length <= 1 && !isLoading && !quota.tutorExhausted && (
            <div className="px-4 pb-2">
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                <Lightbulb className="h-3 w-3" /> Sugestões:
              </p>
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <Button
                    key={s}
                    variant="outline"
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => sendMessage(s)}
                    disabled={isLoading}
                  >
                    {s}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage(inputValue);
            }}
            className="p-4 border-t bg-background"
          >
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={quota.tutorExhausted ? "Cota esgotada — faça upgrade" : "Pergunte ao tutor..."}
                disabled={isLoading || quota.tutorExhausted}
                className="flex-1"
              />
              <Button
                type="submit"
                size="icon"
                disabled={isLoading || !inputValue.trim() || quota.tutorExhausted}
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            {!quota.isUnlimited && !quota.tutorExhausted && (
              <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                {quota.tutor_remaining} {quota.tutor_remaining === 1 ? "mensagem restante" : "mensagens restantes"} hoje
              </p>
            )}
          </form>
        </SheetContent>
      </Sheet>

      <PaywallDialog open={showPaywall} onOpenChange={setShowPaywall} />
    </>
  );
}
